import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Container,
  Content,
  LocalAndActions,
  HourAndActions,
  ButtonsContainer,
} from "./styles";

import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ModalConfirmCancelVisit from "../ModalConfirmCancelVisit";
import { useAuth } from "../../hooks/auth";
import api from "../../services/api";
import ModalReSchedule from "../ModalReSchedule";
import { useRefresh } from "../../hooks/refresh";
import { getApiErrorMessage } from "../../utils/authRedirect";
import { getPhotosSliderSettings } from "../../utils/photosSliderSettings";
import LazyPropertyImage from "../LazyPropertyImage";

interface IAddress {
  id: number;
  address: string;
  neighborhood: string;
  city: string;
  uf: string;
}

interface IPhotos {
  id: number;
  url_l: string;
  url_m: string;
  url_s: string;
}

interface IApiResult {
  property_id: number;
  first_name: string;
  title: string;
  date: string;
  address: IAddress;
  photos: IPhotos[];
  schedule_id: number;
  consultant_id: number;
}

type VisitAction = "reschedule" | "cancel";

interface IVisitPropertyCardProps extends IApiResult {
  initialAction?: VisitAction;
  onInitialActionHandled?: () => void;
}

export default function VisitPropertyCard({
  property_id,
  schedule_id,
  consultant_id,
  first_name,
  title,
  date,
  address,
  photos,
  initialAction,
  onInitialActionHandled,
}: IVisitPropertyCardProps) {
  const { access_token } = useAuth();
  const { handleRefresh } = useRefresh();

  const [scheduleID, setScheduleID] = useState<number>();
  const [openModalConfirmCancelVisit, setOpenModalConfirmCancelVisit] =
    useState(false);
  const [loadingCancelScheduleApiRequest, setLoadingCancelScheduleApiRequest] =
    useState(false);

  const [consultantID, setConsultantID] = useState<number>(0);
  const [propertyID, setPropertyID] = useState(0);
  const [scheduleIDToReSchedule, setScheduleIDToReSchedule] = useState(0);

  const [openModalReSchedule, setOpenModalReSchedule] = useState(false);
  const [actionError, setActionError] = useState("");

  const settings = useMemo(
    () => getPhotosSliderSettings(photos.length),
    [photos.length]
  );

  const formatTitle = useMemo(() => {
    if (title.length >= 18) {
      return `${title.substr(0, 18)}...`;
    }

    return title;
  }, [title]);

  const formatAddress = useMemo(() => {
    const addressFormatted = `${address.address}, ${address.neighborhood}, ${address.city} - ${address.uf}`;

    if (addressFormatted.length >= 55) {
      return `${addressFormatted.substr(0, 55)}...`;
    }

    return `${address.address}, ${address.neighborhood}, ${address.city} - ${address.uf}`;
  }, [address]);

  const formatInformation = useMemo(() => {
    return `${date.substring(8, 10)}/${date.substring(5, 7)}/${date.substring(
      0,
      4
    )} - ${date.substring(11, 13)}H`;
  }, [date]);

  const handleOpenModalCancelVisit = useCallback(() => {
    setActionError("");
    setScheduleID(schedule_id);
    setOpenModalConfirmCancelVisit(true);
  }, [schedule_id]);

  const handleCancelVisit = useCallback(async () => {
    setLoadingCancelScheduleApiRequest(true);
    setActionError("");

    api.defaults.headers.authorization = `Bearer ${access_token}`;

    try {
      const response = await api.put(`/api/client/visits/${scheduleID}`);

      if (!response.data?.success) {
        setActionError(
          response.data?.message || "Não foi possível cancelar a visita."
        );
        setOpenModalConfirmCancelVisit(false);
        return;
      }

      setOpenModalConfirmCancelVisit(false);
      handleRefresh();
    } catch (err) {
      setActionError(
        getApiErrorMessage(err, "Não foi possível cancelar a visita.")
      );
      setOpenModalConfirmCancelVisit(false);
    } finally {
      setLoadingCancelScheduleApiRequest(false);
    }
  }, [access_token, scheduleID, handleRefresh]);

  const handleOpenModalReSchedule = useCallback(() => {
    setActionError("");
    setConsultantID(consultant_id);
    setPropertyID(property_id);
    setScheduleIDToReSchedule(schedule_id);

    setOpenModalReSchedule(true);
  }, [consultant_id, property_id, schedule_id]);

  const handleCloseModalReSchedule = useCallback(() => {
    handleRefresh();

    setOpenModalReSchedule(false);
  }, [handleRefresh]);

  useEffect(() => {
    if (!initialAction) {
      return;
    }

    if (initialAction === "reschedule") {
      handleOpenModalReSchedule();
    }

    if (initialAction === "cancel") {
      handleOpenModalCancelVisit();
    }

    onInitialActionHandled?.();
  }, [
    initialAction,
    handleOpenModalReSchedule,
    handleOpenModalCancelVisit,
    onInitialActionHandled,
  ]);

  return (
    <>
      {!!openModalConfirmCancelVisit && (
        <ModalConfirmCancelVisit
          functionToConfirm={handleCancelVisit}
          functionToClose={() => setOpenModalConfirmCancelVisit(false)}
          isLoadingApiRequest={loadingCancelScheduleApiRequest}
        />
      )}

      {!!openModalReSchedule && (
        <ModalReSchedule
          consultantID={consultantID}
          propertyID={propertyID}
          scheduleID={scheduleIDToReSchedule}
          fromDate={date}
          functionToCloseModalReSchedule={handleCloseModalReSchedule}
        />
      )}

      <Container>
        <Content>
          <Slider {...settings}>
            {photos.map((photo) => (
              <LazyPropertyImage
                key={photo.id}
                src={photo.url_m}
                alt="propertyImg"
                loading="lazy"
              />
            ))}
          </Slider>

          <LocalAndActions>
            <h2>{formatTitle}</h2>

            <p>{formatAddress}</p>
          </LocalAndActions>

          <HourAndActions>
            <h2>{formatInformation}</h2>

            <p>
              <b>Consultor: </b>
              {first_name}
            </p>

            <ButtonsContainer>
              {actionError && <p role="alert">{actionError}</p>}
              <button
                id="reSchedule-button"
                onClick={handleOpenModalReSchedule}
              >
                REAGENDAR
              </button>
              <p>OU</p>
              <button id="cancel-button" onClick={handleOpenModalCancelVisit}>
                CANCELAR
              </button>
            </ButtonsContainer>
          </HourAndActions>
        </Content>
      </Container>
    </>
  );
}
